<template>
  <div id="InvoiceMgmt" class="purchase-mgmt-page">
    <div class="page-header">
      <div class="filter-card">
        <div class="filter-row">
          <div class="filter-item">
            <span class="filter-label">创建日期</span>
            <el-date-picker
              v-model="createDate"
              size="small"
              type="daterange"
              range-separator="至"
              start-placeholder="开始"
              end-placeholder="结束"
              class="filter-date"
            ></el-date-picker>
          </div>
          <div class="filter-item">
            <span class="filter-label">店铺</span>
            <el-select v-model="params.shopName" size="small" filterable clearable placeholder="全部">
              <el-option v-for="item in shopOptions" :key="item" :label="item" :value="item"></el-option>
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">供应商</span>
            <el-select v-model="params.supplierId" size="small" filterable clearable placeholder="全部">
              <el-option
                v-for="supplier in enabledSupplierList"
                :key="supplier.supplierId"
                :label="supplier.supplierShortName"
                :value="supplier.supplierId"
              ></el-option>
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">业务类型</span>
            <el-select v-model="params.sourceType" size="small" clearable placeholder="全部">
              <el-option
                v-for="item in sourceTypeOptions"
                :key="item.value || 'all'"
                :label="item.label"
                :value="item.value"
              ></el-option>
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">合同状态</span>
            <el-select v-model="params.contractStatus" size="small" clearable placeholder="全部" class="filter-select-wide">
              <el-option
                v-for="item in contractStatusOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              ></el-option>
            </el-select>
            <el-button type="primary" size="small" icon="el-icon-search" class="query-btn" @click="queryBtn">查询</el-button>
            <el-button size="small" icon="el-icon-tickets" @click="onViewCompanyInvoiceInfo">查看开票信息</el-button>
          </div>
        </div>
      </div>
    </div>

    <div class="table-panel" ref="tablePanel">
      <el-tabs v-model="activeStatusTab" class="status-tabs" @tab-click="onStatusTabChange">
        <el-tab-pane v-for="tab in statusTabs" :key="tab.name" :name="tab.name">
          <span slot="label" class="status-tab-label">
            {{ tab.label }}
            <el-badge
              v-if="tab.showBadge && statusCounts[tab.name] > 0"
              :value="statusCounts[tab.name]"
              class="status-tab-badge"
            ></el-badge>
          </span>
        </el-tab-pane>
      </el-tabs>

      <div class="page-toolbar" ref="contractToolbar">
        <el-button type="primary" size="small" icon="el-icon-document" :disabled="!selectedInvoices.length" @click="onGenerateContract">
          生成采购合同
        </el-button>
        <span v-if="selectedInvoices.length" class="toolbar-tip">已选 {{ selectedInvoices.length }} 单</span>
      </div>

      <div class="table-container">
        <el-table
          ref="invoiceTable"
          :data="tableData"
          border
          stripe
          size="mini"
          :height="screenHeight"
          v-loading="loading"
          element-loading-text="加载中..."
          :row-key="getInvoiceTableRowKey"
          :span-method="spanMethod"
          show-summary
          :summary-method="getTableSummaries"
          cell-class-name="invoiceTableCellClassName"
          header-cell-class-name="table-header-cell"
          class="mgmt-table"
          @selection-change="handleInvoiceSelectionChange"
        >
          <el-table-column
            type="selection"
            width="42"
            align="center"
            fixed="left"
            :selectable="invoiceRowSelectable"
          ></el-table-column>
          <el-table-column label="序号" width="52" align="center" fixed="left">
            <template slot-scope="scope">
              <span v-if="scope.row._invoiceRowSpan > 0">{{ getInvoiceRowIndex(scope.row) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="shopName" label="店铺" width="56" align="center" show-overflow-tooltip class-name="merge-col"></el-table-column>
          <el-table-column prop="supplierName" label="供应商" width="100" show-overflow-tooltip class-name="merge-col"></el-table-column>
          <el-table-column prop="invoiceCompanyName" label="开票单位名称" width="180" show-overflow-tooltip class-name="merge-col"></el-table-column>
          <el-table-column prop="sourceType" label="业务类型" width="72" align="center" class-name="merge-col">
            <template slot-scope="scope">
              <el-tag
                v-if="scope.row.sourceTypeLabel || scope.row.sourceType"
                size="mini"
                :type="getSourceTypeTagType(scope.row.sourceType)"
                effect="plain"
              >
                {{ scope.row.sourceTypeLabel || getSourceTypeLabel(scope.row.sourceType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="invoiceStatus" label="开票状态" width="120" align="center" class-name="merge-col">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.invoiceStatusLabel" size="mini" :type="getInvoiceStatusType(scope.row.invoiceStatus)" effect="light">
                {{ scope.row.invoiceStatusLabel }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="contractStatus" label="合同状态" width="120" align="center" class-name="merge-col">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.contractStatusLabel" size="mini" :type="getContractStatusType(scope.row.contractStatus)" effect="light">
                {{ scope.row.contractStatusLabel }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="projectName" label="项目名称" width="280" show-overflow-tooltip class-name="text-col-20"></el-table-column>
          <el-table-column prop="specModel" label="规格型号" width="280" show-overflow-tooltip class-name="text-col-20"></el-table-column>
          <el-table-column prop="unit" label="单位" width="52" align="center">
            <template slot-scope="scope">{{ scope.row.unit || "个" }}</template>
          </el-table-column>
          <el-table-column prop="quantity" label="数量" width="64" align="right"></el-table-column>
          <el-table-column prop="unitPrice" label="单价" width="80" align="right">
            <template slot-scope="scope">{{ formatUnitPrice(scope.row.unitPrice) }}</template>
          </el-table-column>
          <el-table-column prop="amount" label="金额" width="80" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.amount) }}</template>
          </el-table-column>
          <el-table-column prop="taxRate" label="税率" width="64" align="right">
            <template slot-scope="scope">{{ formatTaxRate(scope.row.taxRate) }}</template>
          </el-table-column>
          <el-table-column prop="taxAmount" label="税额" width="80" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.taxAmount) }}</template>
          </el-table-column>
          <el-table-column prop="totalAmount" label="金额合计" width="88" align="right" class-name="merge-col">
            <template slot-scope="scope">{{ formatMoney(scope.row.totalAmount) }}</template>
          </el-table-column>
          <el-table-column prop="totalTaxAmount" label="税费合计" width="88" align="right" class-name="merge-col">
            <template slot-scope="scope">{{ formatMoney(scope.row.totalTaxAmount) }}</template>
          </el-table-column>
          <el-table-column prop="totalWithTax" label="价税合计" width="88" align="right" class-name="merge-col highlight-amount">
            <template slot-scope="scope">
              <span class="money-text">{{ formatMoney(scope.row.totalWithTax) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="invoiceDate" label="开票日期" width="96" align="center" class-name="merge-col"></el-table-column>
          <el-table-column prop="invoiceFileUrls" label="发票" width="140" align="center" class-name="merge-col">
            <template slot-scope="scope">
              <div v-if="scope.row._invoiceImageUrls.length" class="invoice-file-thumbs">
                <el-image
                  v-for="(url, index) in scope.row._invoiceImageUrls.slice(0, 3)"
                  :key="url + '-' + index"
                  :src="url"
                  :preview-src-list="scope.row._invoiceImageUrls"
                  fit="cover"
                  class="invoice-file-thumb"
                ></el-image>
                <span v-if="scope.row._invoiceImageUrls.length > 3" class="invoice-file-more">+{{ scope.row._invoiceImageUrls.length - 3 }}</span>
              </div>
              <el-button
                v-else-if="scope.row._invoiceFileItems.length"
                type="text"
                size="small"
                class="invoice-file-link"
                @click="onPreviewInvoiceFile(scope.row)"
              >
                {{ getInvoiceFileDisplayName(scope.row) }}
              </el-button>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="createTime" label="创建时间" width="128" align="center" class-name="merge-col">
            <template slot-scope="scope">{{ formatCreateTime(scope.row.createTime) }}</template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" min-width="100" class-name="merge-col">
            <template slot-scope="scope">
              <div v-if="scope.row.remark || scope.row.rejectReason" class="remark-cell">
                <div v-if="scope.row.remark">{{ scope.row.remark }}</div>
                <div v-if="scope.row.rejectReason" class="reject-reason-text">打回原因：{{ scope.row.rejectReason }}</div>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" align="center" fixed="right" class-name="action-col">
            <template slot-scope="scope">
              <div v-if="scope.row._invoiceRowSpan > 0" class="action-cell">
                <el-button
                  v-for="action in getVisibleInvoiceActions(scope.row)"
                  :key="action.key"
                  type="text"
                  size="small"
                  :class="['action-btn', action.btnClass]"
                  @click="handleInvoiceRowAction(action.key, scope.row)"
                >
                  {{ action.label }}
                </el-button>
                <el-dropdown v-if="getMoreInvoiceActions(scope.row).length" trigger="click" @command="cmd => handleInvoiceRowAction(cmd, scope.row)">
                  <el-button type="text" size="small" class="action-btn">
                    更多<i class="el-icon-arrow-down el-icon--right"></i>
                  </el-button>
                  <el-dropdown-menu slot="dropdown" class="invoice-action-dropdown">
                    <el-dropdown-item v-for="action in getMoreInvoiceActions(scope.row)" :key="action.key" :command="action.key" :class="action.btnClass">
                      {{ action.label }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-box">
        <el-pagination
          layout="prev, pager, next, total"
          background
          :page-size="params.pageSize"
          :total="total"
          :current-page="currentPage"
          @current-change="handlerPageChange"
        ></el-pagination>
      </div>
    </div>

    <el-dialog
      :title="invoiceDialogTitle"
      :visible.sync="invoiceDialogVisible"
      width="960px"
      custom-class="pay-order-invoice-dialog"
      :close-on-click-modal="false"
    >
      <div v-if="invoiceDialogEditable" class="invoice-preview-config">
        <div class="invoice-preview-config-row">
          <span class="invoice-preview-config-label">采购供应商</span>
          <span class="invoice-preview-config-value">{{ invoiceForm.supplierName || "-" }}</span>
        </div>
        <div class="invoice-preview-config-row">
          <span class="invoice-preview-config-label">开票信息</span>
          <el-select
            v-model="invoiceForm.invoiceInfoId"
            size="small"
            filterable
            clearable
            placeholder="请选择开票信息"
            class="invoice-preview-config-select"
            :disabled="!invoiceEditSupplierInvoiceInfoList.length"
          >
            <el-option
              v-for="(item, index) in invoiceEditSupplierInvoiceInfoList"
              :key="item.invoiceInfoId"
              :label="getInvoiceInfoOptionLabel(item, index)"
              :value="item.invoiceInfoId"
            ></el-option>
          </el-select>
        </div>
        <div v-if="!invoiceEditSupplierInvoiceInfoList.length" class="invoice-preview-config-tip">
          该供应商暂未配置开票信息，请先在采购供应商管理中维护
        </div>
        <el-descriptions
          v-else-if="selectedInvoiceEditInfo"
          :column="2"
          border
          size="small"
          class="invoice-preview-info-descriptions"
        >
          <el-descriptions-item label="单位名称">{{ displayInvoiceField(selectedInvoiceEditInfo.invoiceCompanyName) }}</el-descriptions-item>
          <el-descriptions-item label="税号">{{ displayInvoiceField(selectedInvoiceEditInfo.invoiceTaxNo) }}</el-descriptions-item>
          <el-descriptions-item label="单位地址">{{ displayInvoiceField(selectedInvoiceEditInfo.invoiceCompanyAddress) }}</el-descriptions-item>
          <el-descriptions-item label="电话">{{ displayInvoiceField(selectedInvoiceEditInfo.invoicePhone) }}</el-descriptions-item>
          <el-descriptions-item label="开户银行">{{ displayInvoiceField(selectedInvoiceEditInfo.invoiceBankName) }}</el-descriptions-item>
          <el-descriptions-item label="银行账户">{{ displayInvoiceField(selectedInvoiceEditInfo.invoiceBankAccount) }}</el-descriptions-item>
          <el-descriptions-item label="收票邮箱" :span="2">{{ displayInvoiceField(selectedInvoiceEditInfo.invoiceEmail) }}</el-descriptions-item>
        </el-descriptions>
      </div>
      <div class="invoice-preview-panel">
        <el-table
          :data="invoiceForm.lines"
          border
          stripe
          size="mini"
          max-height="420"
          show-summary
          :summary-method="getInvoiceSummaries"
          class="invoice-preview-table"
          empty-text="暂无开票明细"
        >
          <el-table-column prop="projectName" label="项目名称" min-width="160" show-overflow-tooltip></el-table-column>
          <el-table-column prop="specModel" label="规格型号" min-width="140" show-overflow-tooltip></el-table-column>
          <el-table-column prop="unit" label="单位" width="64" align="center">
            <template slot-scope="scope">{{ scope.row.unit || "个" }}</template>
          </el-table-column>
          <el-table-column prop="quantity" label="数量" width="80" align="right"></el-table-column>
          <el-table-column prop="unitPrice" label="单价" width="96" align="right">
            <template slot-scope="scope">{{ formatUnitPrice(scope.row.unitPrice) }}</template>
          </el-table-column>
          <el-table-column prop="amount" label="金额" width="96" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.amount) }}</template>
          </el-table-column>
          <el-table-column prop="taxRate" label="税率" width="80" align="right">
            <template slot-scope="scope">{{ formatTaxRate(scope.row.taxRate) }}</template>
          </el-table-column>
          <el-table-column prop="taxAmount" label="税额" width="96" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.taxAmount) }}</template>
          </el-table-column>
        </el-table>
        <div class="invoice-preview-footer">
          <span class="invoice-preview-footer-label">价税合计</span>
          <span class="invoice-preview-footer-value">{{ formatMoney(invoiceTotalWithTax) }}</span>
        </div>
        <div v-if="invoiceDialogEditable" class="invoice-edit-extra">
          <div class="invoice-edit-extra-row">
            <span class="invoice-edit-extra-label">开票日期</span>
            <el-date-picker v-model="invoiceForm.invoiceDate" size="small" type="date" value-format="yyyy-MM-dd" placeholder="选择日期"></el-date-picker>
          </div>
          <div class="invoice-edit-extra-row wide">
            <span class="invoice-edit-extra-label">备注</span>
            <el-input v-model="invoiceForm.remark" size="small" clearable placeholder="请输入备注"></el-input>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="invoiceDialogVisible = false">{{ invoiceDialogEditable ? "取 消" : "关 闭" }}</el-button>
        <el-button v-if="invoiceDialogEditable" type="primary" @click="saveInvoice" :loading="invoiceSaving">保 存</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="关联支付订单"
      :visible.sync="payOrderDialogVisible"
      width="1380px"
      custom-class="invoice-pay-order-dialog"
      :close-on-click-modal="false"
    >
      <el-table
        :data="payOrderTableData"
        border
        stripe
        size="mini"
        max-height="480"
        v-loading="payOrderDialogLoading"
        :span-method="payOrderSpanMethod"
        empty-text="暂无关联支付订单"
      >
        <el-table-column prop="paymentGroupId" label="支付订单编号" width="148" align="center" show-overflow-tooltip class-name="merge-col"></el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="136" align="center" class-name="merge-col">
          <template slot-scope="scope">{{ formatCreateTime(scope.row.createTime) }}</template>
        </el-table-column>
        <el-table-column prop="supplierName" label="供应商" width="100" show-overflow-tooltip class-name="merge-col"></el-table-column>
        <el-table-column prop="styleName" label="产品名称" width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="unitPrice" label="单价" width="72" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.unitPrice) }}</template>
        </el-table-column>
        <el-table-column prop="quantity" label="数量" width="64" align="right"></el-table-column>
        <el-table-column prop="totalPrice" label="总价" width="72" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.totalPrice) }}</template>
        </el-table-column>
        <el-table-column prop="taxRate" label="税率" width="64" align="right">
          <template slot-scope="scope">{{ formatTaxRate(scope.row.taxRate) }}</template>
        </el-table-column>
        <el-table-column prop="taxAmount" label="税额" width="72" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.taxAmount) }}</template>
        </el-table-column>
        <el-table-column prop="freight" label="运费" width="72" align="right" class-name="merge-col">
          <template slot-scope="scope">{{ formatNumber(scope.row.freight) }}</template>
        </el-table-column>
        <el-table-column prop="platformDiscount" label="平台优惠" width="80" align="right" class-name="merge-col">
          <template slot-scope="scope">{{ formatNumber(scope.row.platformDiscount) }}</template>
        </el-table-column>
        <el-table-column prop="returnExchangeFee" label="退换货费" width="80" align="right" class-name="merge-col">
          <template slot-scope="scope">{{ formatNumber(scope.row.returnExchangeFee) }}</template>
        </el-table-column>
        <el-table-column prop="otherFee" label="其他费用" width="80" align="right" class-name="merge-col">
          <template slot-scope="scope">{{ formatNumber(scope.row.otherFee) }}</template>
        </el-table-column>
        <el-table-column prop="payableAmount" label="应付金额" width="88" align="right" class-name="merge-col">
          <template slot-scope="scope">{{ formatMoney(scope.row.payableAmount) }}</template>
        </el-table-column>
        <el-table-column prop="actualPaidAmount" label="实付金额" width="88" align="right" class-name="merge-col">
          <template slot-scope="scope">{{ formatMoney(scope.row.actualPaidAmount) }}</template>
        </el-table-column>
        <el-table-column prop="amountDiff" label="差值" width="80" align="right" class-name="merge-col">
          <template slot-scope="scope">
            <span :class="getAmountDiffClass(scope.row.amountDiff)">{{ formatAmountDiff(scope.row.amountDiff) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="orderStatus" label="订单状态" width="88" align="center" class-name="merge-col">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.orderStatusLabel || scope.row.orderStatus" size="mini" :type="getPayOrderStatusType(scope.row.orderStatus)" effect="light">
              {{ scope.row.orderStatusLabel || scope.row.orderStatus }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="payOrderDialogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="采购合同预览"
      :visible.sync="contractPreviewVisible"
      width="960px"
      custom-class="pay-order-invoice-dialog"
      :close-on-click-modal="false"
    >
      <div class="invoice-preview-panel">
        <div class="contract-preview-meta">
          <span class="contract-preview-meta-label">合同编号</span>
          <span class="contract-preview-meta-value">{{ contractPreviewForm.contractNo || "-" }}</span>
        </div>
        <el-table
          :data="contractPreviewLines"
          border
          stripe
          size="mini"
          max-height="420"
          show-summary
          :summary-method="getContractPreviewSummaries"
          v-loading="contractPreviewLoading"
          class="invoice-preview-table"
          empty-text="暂无合同明细"
        >
          <el-table-column prop="projectName" label="项目名称" min-width="160" show-overflow-tooltip></el-table-column>
          <el-table-column prop="specModel" label="规格型号" min-width="140" show-overflow-tooltip></el-table-column>
          <el-table-column prop="unit" label="单位" width="64" align="center">
            <template slot-scope="scope">{{ scope.row.unit || "个" }}</template>
          </el-table-column>
          <el-table-column prop="quantity" label="数量" width="80" align="right"></el-table-column>
          <el-table-column prop="unitPrice" label="单价" width="96" align="right">
            <template slot-scope="scope">{{ formatUnitPrice(scope.row.unitPrice) }}</template>
          </el-table-column>
          <el-table-column prop="amount" label="金额" width="96" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.amount) }}</template>
          </el-table-column>
          <el-table-column prop="taxRate" label="税率" width="80" align="right">
            <template slot-scope="scope">{{ formatTaxRate(scope.row.taxRate) }}</template>
          </el-table-column>
          <el-table-column prop="taxAmount" label="税额" width="96" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.taxAmount) }}</template>
          </el-table-column>
        </el-table>
        <div class="invoice-preview-footer">
          <span class="invoice-preview-footer-label">价税合计</span>
          <span class="invoice-preview-footer-value">{{ formatMoney(contractPreviewTotalWithTax) }}</span>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="contractPreviewVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveContractPreview" :loading="contractPreviewSaving">确认生成</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="invoiceFileUploadTitle"
      :visible.sync="invoiceFileUploadVisible"
      width="640px"
      custom-class="invoice-file-upload-dialog"
      :close-on-click-modal="false"
      @opened="onInvoiceFileUploadDialogOpened"
      @closed="onInvoiceFileUploadDialogClosed"
    >
      <div ref="invoiceFilePastePanel" class="invoice-file-upload-panel" tabindex="0" v-loading="invoiceFileUploading">
        <div class="invoice-file-upload-list">
          <div v-for="(item, index) in invoiceFileUploadItems" :key="item.url + '-' + index" class="invoice-file-upload-item">
            <el-image v-if="item.isImage" :src="item.url" fit="cover" class="invoice-file-upload-image" :preview-src-list="invoiceFileUploadImageUrls"></el-image>
            <div v-else class="invoice-file-upload-pdf">
              <i class="el-icon-document"></i>
              <span>{{ item.name || "PDF发票" }}</span>
            </div>
            <i class="el-icon-error invoice-file-upload-remove" @click="removeInvoiceFileUploadItem(index)"></i>
          </div>
          <el-upload
            class="invoice-file-upload-add"
            action="/biz/oss/upload"
            name="file"
            accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,image/*,application/pdf"
            :show-file-list="false"
            :before-upload="beforeInvoiceFileUpload"
            :on-success="handleInvoiceFileUploadSuccess"
            :disabled="invoiceFileUploading"
          >
            <div class="invoice-file-upload-trigger">
              <i class="el-icon-plus"></i>
              <span>添加文件</span>
            </div>
          </el-upload>
        </div>
        <div class="invoice-file-upload-tip">支持 JPG、PNG、GIF、WEBP、BMP、PDF；可上传多张，可在本弹窗内 Ctrl+V 粘贴截图</div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="invoiceFileUploadVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveInvoiceFileUpload" :loading="invoiceFileUploadSaving">保 存</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="确认开票"
      :visible.sync="confirmInvoiceVisible"
      width="480px"
      custom-class="invoice-confirm-dialog"
      :close-on-click-modal="false"
    >
      <el-form label-width="88px" size="small">
        <el-form-item label="开票日期">
          <el-date-picker v-model="confirmInvoiceForm.invoiceDate" type="date" value-format="yyyy-MM-dd" placeholder="选择开票日期"></el-date-picker>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="confirmInvoiceVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitConfirmInvoice" :loading="confirmInvoiceSaving">确认开票</el-button>
      </div>
    </el-dialog>

    <el-dialog title="打回" :visible.sync="rejectDialogVisible" width="480px" custom-class="pay-order-reject-dialog" :close-on-click-modal="false">
      <div class="reject-dialog-tip">确认打回到「{{ rejectTargetStatus }}」</div>
      <el-input v-model="rejectReason" type="textarea" :rows="4" maxlength="500" show-word-limit placeholder="请输入打回原因"></el-input>
      <div slot="footer" class="dialog-footer">
        <el-button @click="rejectDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitRejectInvoice" :loading="rejectSubmitting">确 定</el-button>
      </div>
    </el-dialog>

    <el-dialog title="查看开票信息" :visible.sync="invoiceInfoDialogVisible" width="640px" custom-class="invoice-supplier-info-dialog">
      <div class="invoice-info-header">
        <span>{{ invoiceInfoView.supplierName || "-" }}</span>
      </div>
      <div v-if="!hasInvoiceInfoContent(invoiceInfoView.supplierInvoiceInfo)" class="invoice-view-empty">暂无开票信息</div>
      <el-descriptions v-else :column="1" border size="small" class="invoice-info-descriptions">
        <el-descriptions-item label="单位名称">{{ displayInvoiceField(invoiceInfoView.supplierInvoiceInfo.invoiceCompanyName) }}</el-descriptions-item>
        <el-descriptions-item label="税号">{{ displayInvoiceField(invoiceInfoView.supplierInvoiceInfo.invoiceTaxNo) }}</el-descriptions-item>
        <el-descriptions-item label="单位地址">{{ displayInvoiceField(invoiceInfoView.supplierInvoiceInfo.invoiceCompanyAddress) }}</el-descriptions-item>
        <el-descriptions-item label="电话">{{ displayInvoiceField(invoiceInfoView.supplierInvoiceInfo.invoicePhone) }}</el-descriptions-item>
        <el-descriptions-item label="开户银行">{{ displayInvoiceField(invoiceInfoView.supplierInvoiceInfo.invoiceBankName) }}</el-descriptions-item>
        <el-descriptions-item label="银行账户">{{ displayInvoiceField(invoiceInfoView.supplierInvoiceInfo.invoiceBankAccount) }}</el-descriptions-item>
        <el-descriptions-item label="收票邮箱">{{ displayInvoiceField(invoiceInfoView.supplierInvoiceInfo.invoiceEmail) }}</el-descriptions-item>
      </el-descriptions>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="invoiceInfoDialogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="公司开票信息" :visible.sync="companyInvoiceInfoVisible" width="680px" custom-class="company-invoice-info-dialog">
      <el-tabs v-model="activeCompanyInvoiceTab" class="company-invoice-tabs">
        <el-tab-pane v-for="company in companyInvoiceInfoList" :key="company.shopShortName" :label="company.shopName" :name="company.shopShortName">
          <el-descriptions :column="1" border size="small" class="invoice-info-descriptions">
            <el-descriptions-item label="店铺名称">{{ displayInvoiceField(company.shopName) }}</el-descriptions-item>
            <el-descriptions-item label="店铺名称简写">{{ displayInvoiceField(company.shopShortName) }}</el-descriptions-item>
            <el-descriptions-item label="企业名称">{{ displayInvoiceField(company.companyName) }}</el-descriptions-item>
            <el-descriptions-item label="企业税号">{{ displayInvoiceField(company.taxNo) }}</el-descriptions-item>
            <el-descriptions-item label="邮箱">{{ displayInvoiceField(company.email) }}</el-descriptions-item>
            <el-descriptions-item label="企业地址">{{ displayInvoiceField(company.address) }}</el-descriptions-item>
            <el-descriptions-item label="开户银行">{{ displayInvoiceField(company.bankName) }}</el-descriptions-item>
            <el-descriptions-item label="银行账户">{{ displayInvoiceField(company.bankAccount) }}</el-descriptions-item>
          </el-descriptions>
        </el-tab-pane>
      </el-tabs>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="companyInvoiceInfoVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="发票预览"
      :visible.sync="invoiceFilePreviewVisible"
      width="860px"
      custom-class="invoice-file-preview-dialog"
      :close-on-click-modal="false"
    >
      <div class="invoice-file-preview-content">
        <div v-if="invoiceFilePreviewImages.length || invoiceFilePreviewPdfUrl" class="invoice-file-preview-body">
          <div v-if="invoiceFilePreviewImages.length" class="invoice-file-preview-images">
            <el-image
              v-for="(url, index) in invoiceFilePreviewImages"
              :key="url + '-' + index"
              :src="url"
              :preview-src-list="invoiceFilePreviewImages"
              fit="contain"
              class="invoice-file-preview-full"
            ></el-image>
          </div>
          <iframe v-if="invoiceFilePreviewPdfUrl" :src="invoiceFilePreviewPdfUrl" class="invoice-file-preview-frame"></iframe>
        </div>
        <div v-else class="invoice-file-preview-empty">暂无发票文件</div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="invoiceFilePreviewVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="操作记录" :visible.sync="opreateLogVisible" width="1200px">
      <el-table :data="operateHistory" border stripe size="mini" max-height="420" class="record-table">
        <el-table-column label="序号" width="50" align="center">
          <template slot-scope="scope">{{ scope.$index + 1 }}</template>
        </el-table-column>
        <el-table-column prop="opreateUserName" label="操作人" width="90"></el-table-column>
        <el-table-column prop="opreateTimeLabel" label="操作时间" width="170"></el-table-column>
        <el-table-column prop="opreateTypeLabel" label="操作类型" width="120"></el-table-column>
        <el-table-column prop="bussinessId" label="业务ID" width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="content" label="操作详情" min-width="240" show-overflow-tooltip></el-table-column>
      </el-table>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="opreateLogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { invoiceModules, payOrderModules, purchaseContractModules, requestModules, ossModules } from "@r";
import {
  getPayOrderStatusTagType,
  PAY_ORDER_SOURCE_TYPE,
  PAY_ORDER_SOURCE_TYPE_FILTER_OPTIONS,
  getPayOrderSourceTypeLabel,
  normalizePayOrderSourceType,
} from "../../constants/payOrderStatus";

const INVOICE_MERGE_PROPS = [
  "shopName",
  "supplierName",
  "invoiceCompanyName",
  "sourceType",
  "sourceTypeLabel",
  "invoiceStatus",
  "contractStatus",
  "totalAmount",
  "totalTaxAmount",
  "totalWithTax",
  "invoiceDate",
  "invoiceFileUrls",
  "createTime",
  "remark",
];

const STATUS_TABS = [
  { name: "all", label: "全部", showBadge: false },
  { name: "开票信息待确认", label: "开票信息待确认", showBadge: true },
  { name: "待开票信息审核", label: "开票信息审核", showBadge: true },
  { name: "待开票", label: "待开票", showBadge: true },
  { name: "待发票审核", label: "待发票审核", showBadge: true },
  { name: "已开票", label: "已开票", showBadge: false },
  { name: "已作废", label: "已作废", showBadge: false },
];

const SHOP_OPTIONS = ["灵柚", "萧发", "誉腾", "甄昂", "众联", "杭傲"];

const INVOICE_STATUS = {
  PENDING: 0,
  INVOICED: 1,
  VOID: 2,
  INFO_REVIEW: 3,
  INVOICE_REVIEW: 4,
  INFO_CONFIRM: 5,
};

const REVIEWABLE_INVOICE_STATUSES = [
  INVOICE_STATUS.INFO_CONFIRM,
  INVOICE_STATUS.INFO_REVIEW,
  INVOICE_STATUS.PENDING,
  INVOICE_STATUS.INVOICE_REVIEW,
];

const CONTRACT_STATUS = {
  PENDING: 0,
  SIGNED: 1,
  VOID: 2,
  INFO_REVIEW: 3,
  CONTRACT_REVIEW: 4,
  CONFIG: 5,
};

const CONTRACT_STATUS_FILTER_OPTIONS = [
  { label: "未生成合同", value: "未生成合同" },
  { label: "待配置合同信息", value: "待配置合同信息" },
  { label: "待合同信息审核", value: "待合同信息审核" },
  { label: "待签署合同", value: "待签署合同" },
  { label: "待合同审核", value: "待合同审核" },
  { label: "已签署合同", value: "已签署合同" },
];

const COMPANY_INVOICE_INFO_LIST = [
  {
    shopName: "灵柚",
    shopShortName: "LY",
    companyName: "杭州灵柚电子商务有限公司",
    taxNo: "91330109MA2KKKEU79",
    email: "niuhengqian@163.com",
    address: "浙江省杭州市拱墅区祥符街道祥园路39号1幢211室",
    bankName: "",
    bankAccount: "",
  },
  {
    shopName: "萧发",
    shopShortName: "XF",
    companyName: "杭州萧发电子商务有限公司",
    taxNo: "91330106MAEUF7JH0X",
    email: "18157178235@163.com",
    address: "浙江省杭州市西湖区翠苑街道文三路477号华星科技大厦A2974室",
    bankName: "中国建设银行股份有限公司杭州延安支行",
    bankAccount: "33050161793500001007",
  },
  {
    shopName: "甄昂",
    shopShortName: "ZA",
    companyName: "杭州臻昂电子商贸有限责任公司",
    taxNo: "91330106MACUKNAX1Y",
    email: "",
    address: "浙江省杭州市西湖区翠苑街道翠苑二区8-2幢配套用房A1664室",
    bankName: "中国建设银行股份有限公司杭州湖墅支行",
    bankAccount: "33050161644600001161",
  },
  {
    shopName: "众联",
    shopShortName: "ZL",
    companyName: "杭州众联云帆信息技术有限公司",
    taxNo: "91330105MAK5HKL84A",
    email: "",
    address: "",
    bankName: "",
    bankAccount: "",
  },
  {
    shopName: "誉腾",
    shopShortName: "YT",
    companyName: "杭州誉腾电子商务有限公司",
    taxNo: "91330106MAEQND0E1H",
    email: "18157178235@163.com",
    address: "浙江省杭州市西湖区翠苑街道文三路477号华星科技大厦A3267室",
    bankName: "",
    bankAccount: "",
  },
];

const PAY_ORDER_VIEW_MERGE_PROPS = [
  "paymentGroupId",
  "createTime",
  "supplierName",
  "freight",
  "platformDiscount",
  "returnExchangeFee",
  "otherFee",
  "payableAmount",
  "actualPaidAmount",
  "amountDiff",
  "orderStatus",
  "orderStatusLabel",
];

export default {
  data() {
    return {
      tableData: [],
      invoiceList: [],
      total: 0,
      currentPage: 1,
      loading: false,
      activeStatusTab: "all",
      statusTabs: STATUS_TABS,
      statusCounts: {
        开票信息待确认: 0,
        待开票信息审核: 0,
        待开票: 0,
        待发票审核: 0,
      },
      createDate: [],
      supplierList: [],
      shopOptions: SHOP_OPTIONS,
      sourceTypeOptions: PAY_ORDER_SOURCE_TYPE_FILTER_OPTIONS,
      contractStatusOptions: CONTRACT_STATUS_FILTER_OPTIONS,
      params: {
        beginIndex: 0,
        pageSize: 50,
        shopName: "",
        supplierId: "",
        sourceType: "",
        contractStatus: "",
      },
      screenHeight: 400,
      invoiceDialogVisible: false,
      invoiceDialogEditable: false,
      invoiceDialogTitle: "发票详情",
      invoiceSaving: false,
      invoiceForm: {
        _id: "",
        invoiceNo: "",
        supplierId: "",
        supplierName: "",
        invoiceDate: "",
        remark: "",
        invoiceTaxRate: 0,
        invoiceInfoId: "",
        supplierInvoiceInfoList: [],
        paymentGroupIds: [],
        lines: [],
      },
      payOrderDialogVisible: false,
      payOrderDialogLoading: false,
      payOrderTableData: [],
      selectedInvoices: [],
      contractPreviewVisible: false,
      contractPreviewLoading: false,
      contractPreviewSaving: false,
      contractPreviewLines: [],
      contractPreviewForm: {
        contractNo: "",
        supplierId: "",
        supplierName: "",
        invoiceIds: [],
      },
      invoiceFileUploadVisible: false,
      invoiceFileUploadTitle: "上传发票",
      invoiceFileUploadSaving: false,
      invoiceFileUploading: false,
      invoiceFileUploadRow: null,
      invoiceFileUploadItems: [],
      confirmInvoiceVisible: false,
      confirmInvoiceSaving: false,
      confirmInvoiceRow: null,
      confirmInvoiceForm: {
        invoiceDate: "",
      },
      invoiceFilePreviewVisible: false,
      invoiceFilePreviewImages: [],
      invoiceFilePreviewPdfUrl: "",
      invoiceInfoDialogVisible: false,
      invoiceInfoView: {
        supplierName: "",
        invoiceTaxRate: 0,
        supplierInvoiceInfo: null,
      },
      companyInvoiceInfoVisible: false,
      activeCompanyInvoiceTab: "LY",
      companyInvoiceInfoList: COMPANY_INVOICE_INFO_LIST,
      rejectDialogVisible: false,
      rejectSubmitting: false,
      rejectInvoiceId: "",
      rejectTargetStatus: "",
      rejectReason: "",
      opreateLogVisible: false,
      operateHistory: [],
    };
  },
  computed: {
    invoiceTotalWithTax() {
      let totalAmount = this.invoiceForm.lines.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
      let totalTaxAmount = this.invoiceForm.lines.reduce((sum, row) => sum + (parseFloat(row.taxAmount) || 0), 0);
      return parseFloat((totalAmount + totalTaxAmount).toFixed(2));
    },
    contractPreviewTotalWithTax() {
      let totalAmount = this.contractPreviewLines.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
      let totalTaxAmount = this.contractPreviewLines.reduce((sum, row) => sum + (parseFloat(row.taxAmount) || 0), 0);
      return parseFloat((totalAmount + totalTaxAmount).toFixed(2));
    },
    enabledSupplierList() {
      return (this.supplierList || []).filter(supplier => supplier.status == 1);
    },
    invoiceEditSupplierInvoiceInfoList() {
      return this.invoiceForm.supplierInvoiceInfoList || [];
    },
    selectedInvoiceEditInfo() {
      let list = this.invoiceEditSupplierInvoiceInfoList;
      if (!list.length) {
        return null;
      }
      let selected = list.find(item => item.invoiceInfoId === this.invoiceForm.invoiceInfoId);
      return selected || list[0];
    },
    invoiceFileUploadImageUrls() {
      return this.invoiceFileUploadItems.filter(item => item.isImage).map(item => item.url);
    },
  },
  mounted() {
    this.fetchSuppliers();
    this.getData();
    this.relayoutTable();
    this.$eventBus.$on("resize", this.relayoutTable);
  },
  beforeDestroy() {
    this.$eventBus.$off("resize", this.relayoutTable);
    document.removeEventListener("paste", this.handleInvoiceFilePaste);
  },
  methods: {
    fetchSuppliers() {
      requestModules.fetchSuppliers({ beginIndex: 0, pageSize: 1000 }).then(res => {
        this.supplierList = res.ldata || [];
      });
    },
    calculateHeight() {
      const panel = this.$refs.tablePanel;
      if (!panel) {
        return;
      }
      const container = panel.querySelector(".table-container");
      if (container && container.clientHeight > 0) {
        this.screenHeight = Math.max(container.clientHeight, 200);
        return;
      }
      const tabs = panel.querySelector(".status-tabs");
      const pagination = panel.querySelector(".pagination-box");
      const toolbar = this.$refs.contractToolbar;
      const panelStyle = window.getComputedStyle(panel);
      const paddingTop = parseFloat(panelStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(panelStyle.paddingBottom) || 0;
      const tabsHeight = tabs ? tabs.offsetHeight + 12 : 0;
      const paginationHeight = pagination ? pagination.offsetHeight : 0;
      const toolbarHeight = toolbar ? toolbar.offsetHeight + 22 : 0;
      const availableHeight = panel.clientHeight - paddingTop - paddingBottom - tabsHeight - paginationHeight - toolbarHeight;
      this.screenHeight = Math.max(availableHeight, 200);
    },
    relayoutTable() {
      this.$nextTick(() => {
        this.calculateHeight();
        this.$nextTick(() => {
          if (this.$refs.invoiceTable && this.$refs.invoiceTable.doLayout) {
            this.$refs.invoiceTable.doLayout();
          }
        });
      });
    },
    formatMoney(value) {
      if (value === "" || value === null || value === undefined) {
        return "0.00";
      }
      return parseFloat(value).toFixed(2);
    },
    formatUnitPrice(value) {
      if (value === "" || value === null || value === undefined) {
        return "0.00000";
      }
      return parseFloat(value).toFixed(5);
    },
    formatNumber(value) {
      if (value === "" || value === null || value === undefined) {
        return "";
      }
      return parseFloat(value).toFixed(2);
    },
    formatAmountDiff(value) {
      let diff = parseFloat(value) || 0;
      if (diff > 0) {
        return "+" + this.formatNumber(diff);
      }
      return this.formatNumber(diff);
    },
    getAmountDiffClass(value) {
      let diff = parseFloat(value) || 0;
      if (diff > 0) {
        return "positive";
      }
      if (diff < 0) {
        return "negative";
      }
      return "zero";
    },
    formatTaxRate(value) {
      let taxRate = parseFloat(value);
      if (isNaN(taxRate)) {
        return "0%";
      }
      return (taxRate * 100).toFixed(0) + "%";
    },
    formatCreateTime(time) {
      if (!time) return "";
      let date = new Date(Number(time));
      if (isNaN(date.getTime())) return "";
      return date.format("yyyy-MM-dd hh:mm");
    },
    buildQueryParams() {
      let query = {
        shopName: this.params.shopName || "",
        supplierId: this.params.supplierId,
        sourceType: this.params.sourceType || "",
        contractStatus: this.params.contractStatus || "",
        beginIndex: this.params.beginIndex,
        pageSize: this.params.pageSize,
        invoiceStatusTab: this.activeStatusTab,
      };
      if (this.createDate && this.createDate.length === 2) {
        query.beginCreateDate = this.createDate[0].format("yyyy-MM-dd");
        query.endCreateDate = this.createDate[1].format("yyyy-MM-dd");
      }
      return query;
    },
    getSourceTypeLabel(sourceType) {
      return getPayOrderSourceTypeLabel(sourceType);
    },
    getSourceTypeTagType(sourceType) {
      if (sourceType === PAY_ORDER_SOURCE_TYPE.PURCHASE_ORDER) {
        return "success";
      }
      return "info";
    },
    onStatusTabChange(tab) {
      this.activeStatusTab = tab.name;
      this.params.beginIndex = 0;
      this.currentPage = 1;
      this.clearInvoiceSelection();
      this.getData();
    },
    getInvoiceRowKey(row) {
      if (row._rowKey) {
        return row._rowKey;
      }
      if (row._id) {
        return row._id;
      }
      return (row.paymentGroupIds || []).join("|");
    },
    getInvoiceTableRowKey(row) {
      return row._tableRowKey || this.getInvoiceRowKey(row);
    },
    getInvoiceStatusType(invoiceStatus) {
      if (invoiceStatus === INVOICE_STATUS.INFO_CONFIRM) {
        return "warning";
      }
      if (invoiceStatus === INVOICE_STATUS.INFO_REVIEW) {
        return "info";
      }
      if (invoiceStatus === INVOICE_STATUS.PENDING) {
        return "warning";
      }
      if (invoiceStatus === INVOICE_STATUS.INVOICE_REVIEW) {
        return "warning";
      }
      if (invoiceStatus === INVOICE_STATUS.INVOICED) {
        return "success";
      }
      if (invoiceStatus === INVOICE_STATUS.VOID) {
        return "info";
      }
      return "";
    },
    getContractStatusType(contractStatus) {
      if (contractStatus === CONTRACT_STATUS.CONFIG) {
        return "info";
      }
      if (contractStatus === CONTRACT_STATUS.INFO_REVIEW) {
        return "info";
      }
      if (contractStatus === CONTRACT_STATUS.PENDING) {
        return "warning";
      }
      if (contractStatus === CONTRACT_STATUS.CONTRACT_REVIEW) {
        return "warning";
      }
      if (contractStatus === CONTRACT_STATUS.SIGNED) {
        return "success";
      }
      if (contractStatus === CONTRACT_STATUS.VOID) {
        return "info";
      }
      return "info";
    },
    canEditInvoice(row) {
      return (
        !!row._id &&
        [
          INVOICE_STATUS.INFO_CONFIRM,
          INVOICE_STATUS.INFO_REVIEW,
          INVOICE_STATUS.PENDING,
          INVOICE_STATUS.INVOICE_REVIEW,
          INVOICE_STATUS.INVOICED,
        ].includes(row.invoiceStatus)
      );
    },
    canUploadInvoice(row) {
      return !!row._id && [INVOICE_STATUS.PENDING, INVOICE_STATUS.INVOICED].includes(row.invoiceStatus);
    },
    getUploadInvoiceButtonLabel(row) {
      return this.getInvoiceFileItems(row).length ? "更新发票" : "上传发票";
    },
    canReviewInvoice(row) {
      return !!row._id && REVIEWABLE_INVOICE_STATUSES.includes(row.invoiceStatus);
    },
    canRejectInvoice(row) {
      return (
        !!row._id &&
        [INVOICE_STATUS.INFO_REVIEW, INVOICE_STATUS.PENDING, INVOICE_STATUS.INVOICE_REVIEW].includes(row.invoiceStatus)
      );
    },
    getInvoicePassButtonLabel(invoiceStatus) {
      const map = {
        [INVOICE_STATUS.INFO_CONFIRM]: "确认信息",
        [INVOICE_STATUS.INFO_REVIEW]: "审核通过",
        [INVOICE_STATUS.PENDING]: "提交发票审核",
        [INVOICE_STATUS.INVOICE_REVIEW]: "审核通过",
      };
      return map[invoiceStatus] || "通过";
    },
    getInvoiceRowActions(row) {
      const actions = [];
      if (this.canReviewInvoice(row)) {
        actions.push({ key: "approve", label: this.getInvoicePassButtonLabel(row.invoiceStatus), btnClass: "success" });
      }
      if (this.canRejectInvoice(row)) {
        actions.push({ key: "reject", label: "打回", btnClass: "warning" });
      }
      if (this.canEditInvoice(row)) {
        actions.push({ key: "edit", label: "编辑", btnClass: "" });
      }
      if (this.canUploadInvoice(row)) {
        actions.push({ key: "upload", label: this.getUploadInvoiceButtonLabel(row), btnClass: "" });
      }
      actions.push({ key: "operateLog", label: "操作记录", btnClass: "" });
      actions.push({ key: "viewInvoiceInfo", label: "查看开票信息", btnClass: "" });
      actions.push({ key: "viewPayOrders", label: "查看支付订单", btnClass: "" });
      if (this.canVoidInvoice(row)) {
        actions.push({ key: "void", label: "作废", btnClass: "danger" });
      }
      if (this.canDeleteInvoice(row)) {
        actions.push({ key: "delete", label: "删除", btnClass: "danger" });
      }
      return actions;
    },
    getVisibleInvoiceActions(row) {
      return this.getInvoiceRowActions(row).slice(0, 2);
    },
    getMoreInvoiceActions(row) {
      return this.getInvoiceRowActions(row).slice(2);
    },
    handleInvoiceRowAction(command, row) {
      if (command === "approve") {
        this.onApproveInvoice(row);
      } else if (command === "reject") {
        this.onRejectInvoice(row);
      } else if (command === "edit") {
        this.onEditInvoice(row);
      } else if (command === "upload") {
        this.onUploadInvoiceFile(row);
      } else if (command === "operateLog") {
        this.showOpreateLog(row);
      } else if (command === "viewInvoiceInfo") {
        this.onViewInvoiceInfo(row);
      } else if (command === "viewPayOrders") {
        this.onViewPayOrders(row);
      } else if (command === "void") {
        this.onVoidInvoice(row);
      } else if (command === "delete") {
        this.onDeleteInvoice(row);
      }
    },
    canVoidInvoice(row) {
      return !!row._id && row.invoiceStatus === INVOICE_STATUS.INFO_CONFIRM;
    },
    canDeleteInvoice(row) {
      return !!row._id && row.invoiceStatus === INVOICE_STATUS.VOID;
    },
    getData() {
      this.loading = true;
      invoiceModules
        .fetchInvoices(this.buildQueryParams())
        .then(res => {
          this.invoiceList = res.ldata || [];
          this.tableData = this.expandInvoiceList(this.invoiceList);
          this.total = res.total || 0;
          if (res.mdata && res.mdata.statusCounts) {
            this.statusCounts = {
              ...this.statusCounts,
              ...res.mdata.statusCounts,
            };
          }
        })
        .catch(() => {
          this.$message.error("查询失败");
        })
        .finally(() => {
          this.loading = false;
          this.clearInvoiceSelection();
          this.relayoutTable();
        });
    },
    queryBtn() {
      this.params.beginIndex = 0;
      this.currentPage = 1;
      this.getData();
    },
    handlerPageChange(page) {
      this.currentPage = page;
      this.params.beginIndex = (page - 1) * this.params.pageSize;
      this.getData();
    },
    expandInvoiceList(invoices) {
      let result = [];
      (invoices || []).forEach((invoice, invoiceIndex) => {
        let lines = invoice.lines && invoice.lines.length ? invoice.lines : [{}];
        let rowKey = invoice._id || (invoice.paymentGroupIds || []).join("|") || `pending-${invoiceIndex}`;
        let files = this.resolveInvoiceFileLists(invoice);
        let invoiceFileItems = this.buildInvoiceFileItems(files.urls, files.names);
        let invoiceImageUrls = invoiceFileItems.filter(item => item.isImage).map(item => item.url);
        lines.forEach((line, index) => {
          result.push({
            _id: invoice._id,
            _rowKey: rowKey,
            _tableRowKey: rowKey + "-" + index,
            invoiceId: invoice.invoiceId,
            invoiceNo: invoice.invoiceNo || "",
            invoiceStatus: invoice.invoiceStatus != null ? invoice.invoiceStatus : INVOICE_STATUS.INVOICED,
            invoiceStatusLabel: invoice.invoiceStatusLabel || "",
            contractStatus: invoice.contractStatus,
            contractStatusLabel: invoice.contractStatusLabel || "",
            shopName: invoice.shopName || "",
            supplierId: invoice.supplierId,
            supplierName: invoice.supplierName || "",
            invoiceCompanyName:
              (invoice.supplierInvoiceInfo && invoice.supplierInvoiceInfo.invoiceCompanyName) || "",
            sourceType: invoice.sourceType || "",
            sourceTypeLabel: invoice.sourceTypeLabel || "",
            paymentGroupIds: invoice.paymentGroupIds || [],
            totalAmount: invoice.totalAmount,
            totalTaxAmount: invoice.totalTaxAmount,
            totalWithTax: invoice.totalWithTax,
            invoiceDate: invoice.invoiceDate || "",
            invoiceFileUrls: files.urls,
            invoiceFileNames: files.names,
            _invoiceFileItems: invoiceFileItems,
            _invoiceImageUrls: invoiceImageUrls,
            createTime: invoice.createTime,
            remark: invoice.remark || "",
            rejectReason: invoice.rejectReason || "",
            supplierInvoiceInfo: invoice.supplierInvoiceInfo || null,
            projectName: line.projectName || "",
            specModel: line.specModel || "",
            unit: line.unit || "个",
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            amount: line.amount,
            taxRate: line.taxRate,
            taxAmount: line.taxAmount,
            _invoiceRowSpan: index === 0 ? lines.length : 0,
          });
        });
      });
      return result;
    },
    getInvoiceRowIndex(row) {
      let invoiceIndex = 0;
      for (let i = 0; i < this.tableData.length; i++) {
        if (this.tableData[i]._invoiceRowSpan > 0) {
          invoiceIndex++;
          if (this.getInvoiceRowKey(this.tableData[i]) === this.getInvoiceRowKey(row)) {
            return this.params.beginIndex + invoiceIndex;
          }
        }
      }
      return "";
    },
    invoiceTableCellClassName({ row, column }) {
      let classNames = [];
      if (INVOICE_MERGE_PROPS.includes(column.property)) {
        classNames.push("merge-col");
      }
      if (column.type === "selection" || column.label === "序号" || column.label === "操作") {
        classNames.push("merge-col");
      }
      if (column.property === "totalWithTax") {
        classNames.push("highlight-amount");
      }
      if (column.label === "操作") {
        classNames.push("action-col");
      }
      return classNames.join(" ");
    },
    spanMethod({ row, column }) {
      if (INVOICE_MERGE_PROPS.includes(column.property)) {
        let rowspan = row._invoiceRowSpan || 0;
        return { rowspan, colspan: rowspan > 0 ? 1 : 0 };
      }
      if (column.type === "selection" || column.label === "序号" || column.label === "操作") {
        let rowspan = row._invoiceRowSpan || 0;
        return { rowspan, colspan: rowspan > 0 ? 1 : 0 };
      }
      return { rowspan: 1, colspan: 1 };
    },
    invoiceRowSelectable(row) {
      return row._invoiceRowSpan > 0 && !!row._id && row.invoiceStatus !== INVOICE_STATUS.VOID;
    },
    handleInvoiceSelectionChange(selection) {
      let selectedMap = {};
      this.selectedInvoices = (selection || []).filter(row => {
        if (!row._id || row._invoiceRowSpan <= 0 || selectedMap[row._id]) {
          return false;
        }
        selectedMap[row._id] = true;
        return true;
      });
    },
    clearInvoiceSelection() {
      this.selectedInvoices = [];
      if (this.$refs.invoiceTable) {
        this.$refs.invoiceTable.clearSelection();
      }
    },
    onGenerateContract() {
      if (!this.selectedInvoices.length) {
        this.$message.warning("请先选择发票");
        return;
      }
      if (!this.validateContractSelectionSameSourceType()) {
        return;
      }
      let invoiceIds = this.selectedInvoices.map(row => row._id);
      this.contractPreviewLoading = true;
      this.contractPreviewVisible = true;
      this.contractPreviewLines = [];
      purchaseContractModules
        .previewPurchaseContractFromInvoices({
          invoiceIds: JSON.stringify(invoiceIds),
        })
        .then(res => {
          if (res.result !== 1) {
            this.contractPreviewVisible = false;
            this.$message.error(res.message || "生成预览失败");
            return;
          }
          let mdata = res.mdata || {};
          this.contractPreviewForm = {
            contractNo: mdata.contractNo || "",
            supplierId: mdata.supplierId || "",
            supplierName: mdata.supplierName || "",
            invoiceIds: mdata.invoiceIds || invoiceIds,
          };
          this.contractPreviewLines = res.ldata || [];
        })
        .catch(() => {
          this.contractPreviewVisible = false;
          this.$message.error("生成预览失败");
        })
        .finally(() => {
          this.contractPreviewLoading = false;
        });
    },
    validateContractSelectionSameSourceType() {
      let sourceTypes = new Set();
      this.selectedInvoices.forEach(row => {
        sourceTypes.add(normalizePayOrderSourceType(row.sourceType));
      });
      if (sourceTypes.size > 1) {
        this.$message.error("请选择同一业务类型的发票");
        return false;
      }
      return true;
    },
    getContractPreviewSummaries(param) {
      const { columns, data } = param;
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (column.property === "amount") {
          sums[index] = this.formatMoney(data.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0));
          return;
        }
        if (column.property === "taxAmount") {
          sums[index] = this.formatMoney(data.reduce((sum, row) => sum + (parseFloat(row.taxAmount) || 0), 0));
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    saveContractPreview() {
      if (!this.contractPreviewLines.length) {
        this.$message.warning("合同明细为空");
        return;
      }
      this.contractPreviewSaving = true;
      purchaseContractModules
        .createPurchaseContractFromInvoices({
          contractNo: this.contractPreviewForm.contractNo || "",
          supplierId: this.contractPreviewForm.supplierId || "",
          supplierName: this.contractPreviewForm.supplierName || "",
          invoiceIds: JSON.stringify(this.contractPreviewForm.invoiceIds || []),
          lines: JSON.stringify(this.contractPreviewLines),
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "生成采购合同失败");
            return;
          }
          this.$message.success("采购合同已生成");
          this.contractPreviewVisible = false;
          this.clearInvoiceSelection();
        })
        .catch(() => {
          this.$message.error("生成采购合同失败");
        })
        .finally(() => {
          this.contractPreviewSaving = false;
        });
    },
    loadInvoiceDetail(row, editable) {
      invoiceModules.fetchInvoice({ _id: row._id }).then(res => {
        if (res.result !== 1 || !res.ldata || !res.ldata.length) {
          this.$message.error(res.message || "加载发票失败");
          return;
        }
        let invoice = res.ldata[0];
        let supplier = this.supplierList.find(item => item.supplierId == invoice.supplierId);
        let supplierInvoiceInfoList = this.buildSupplierInvoiceInfoList(supplier);
        let invoiceInfoId = "";
        if (invoice.supplierInvoiceInfo && invoice.supplierInvoiceInfo.invoiceInfoId) {
          invoiceInfoId = invoice.supplierInvoiceInfo.invoiceInfoId;
        } else if (supplierInvoiceInfoList.length) {
          invoiceInfoId = supplierInvoiceInfoList[0].invoiceInfoId;
        }
        this.invoiceForm = {
          _id: invoice._id,
          invoiceNo: invoice.invoiceNo || "",
          supplierId: invoice.supplierId || "",
          supplierName: invoice.supplierName || "",
          invoiceDate: invoice.invoiceDate || "",
          remark: invoice.remark || "",
          invoiceTaxRate: invoice.invoiceTaxRate != null ? invoice.invoiceTaxRate : supplier ? supplier.invoiceTaxRate : 0,
          invoiceInfoId,
          supplierInvoiceInfoList,
          paymentGroupIds: invoice.paymentGroupIds || [],
          lines: invoice.lines || [],
        };
        this.invoiceDialogEditable = editable;
        this.invoiceDialogTitle = editable ? "编辑发票" : "发票详情";
        this.invoiceDialogVisible = true;
      });
    },
    onEditInvoice(row) {
      this.loadInvoiceDetail(row, true);
    },
    displayInvoiceField(value) {
      return value ? value : "-";
    },
    formatInvoiceTaxRate(value) {
      let taxRate = parseFloat(value);
      if (isNaN(taxRate) || taxRate <= 0) {
        return "0%";
      }
      return (taxRate * 100).toFixed(0) + "%";
    },
    hasInvoiceInfoContent(info) {
      if (!info) {
        return false;
      }
      return !!(
        info.invoiceCompanyName ||
        info.invoiceTaxNo ||
        info.invoiceCompanyAddress ||
        info.invoicePhone ||
        info.invoiceBankName ||
        info.invoiceBankAccount ||
        info.invoiceEmail
      );
    },
    resolveSupplierInvoiceInfoFromSupplier(supplier) {
      if (!supplier) {
        return null;
      }
      if (supplier.invoiceInfoList && supplier.invoiceInfoList.length) {
        return { ...supplier.invoiceInfoList[0] };
      }
      if (
        supplier.invoiceCompanyName ||
        supplier.invoiceTaxNo ||
        supplier.invoiceCompanyAddress ||
        supplier.invoicePhone ||
        supplier.invoiceBankName ||
        supplier.invoiceBankAccount ||
        supplier.invoiceEmail
      ) {
        return {
          invoiceCompanyName: supplier.invoiceCompanyName || "",
          invoiceTaxNo: supplier.invoiceTaxNo || "",
          invoiceCompanyAddress: supplier.invoiceCompanyAddress || "",
          invoicePhone: supplier.invoicePhone || "",
          invoiceBankName: supplier.invoiceBankName || "",
          invoiceBankAccount: supplier.invoiceBankAccount || "",
          invoiceEmail: supplier.invoiceEmail || "",
        };
      }
      return null;
    },
    buildSupplierInvoiceInfoList(supplier) {
      if (!supplier) {
        return [];
      }
      if (supplier.invoiceInfoList && supplier.invoiceInfoList.length) {
        return supplier.invoiceInfoList.map(item => ({ ...item }));
      }
      let legacyInfo = this.resolveSupplierInvoiceInfoFromSupplier(supplier);
      if (legacyInfo) {
        return [
          {
            invoiceInfoId: "legacy",
            invoiceCompanyName: legacyInfo.invoiceCompanyName || "",
            invoiceTaxNo: legacyInfo.invoiceTaxNo || "",
            invoiceCompanyAddress: legacyInfo.invoiceCompanyAddress || "",
            invoicePhone: legacyInfo.invoicePhone || "",
            invoiceBankName: legacyInfo.invoiceBankName || "",
            invoiceBankAccount: legacyInfo.invoiceBankAccount || "",
            invoiceEmail: legacyInfo.invoiceEmail || "",
          },
        ];
      }
      return [];
    },
    getInvoiceInfoOptionLabel(item, index) {
      let parts = [];
      if (item.invoiceCompanyName) {
        parts.push(item.invoiceCompanyName);
      }
      if (item.invoiceTaxNo) {
        parts.push("税号:" + item.invoiceTaxNo);
      }
      if (item.invoiceBankName) {
        parts.push(item.invoiceBankName);
      }
      if (item.invoiceEmail) {
        parts.push(item.invoiceEmail);
      }
      if (parts.length) {
        return "开票信息" + (index + 1) + " - " + parts.join(" | ");
      }
      return "开票信息" + (index + 1);
    },
    onViewInvoiceInfo(row) {
      let invoice = this.invoiceList.find(item => item._id === row._id);
      let supplier = this.supplierList.find(item => item.supplierId == row.supplierId);
      let supplierInvoiceInfo = invoice && invoice.supplierInvoiceInfo ? { ...invoice.supplierInvoiceInfo } : null;
      if (!this.hasInvoiceInfoContent(supplierInvoiceInfo)) {
        supplierInvoiceInfo = this.resolveSupplierInvoiceInfoFromSupplier(supplier);
      }
      let invoiceTaxRate = supplier && supplier.invoiceTaxRate != null ? supplier.invoiceTaxRate : 0;
      this.invoiceInfoView = {
        supplierName: row.supplierName || "",
        invoiceTaxRate,
        supplierInvoiceInfo,
      };
      this.invoiceInfoDialogVisible = true;
    },
    onViewCompanyInvoiceInfo() {
      this.activeCompanyInvoiceTab = this.companyInvoiceInfoList[0].shopShortName;
      this.companyInvoiceInfoVisible = true;
    },
    onViewPayOrders(row) {
      let paymentGroupIds = row.paymentGroupIds || [];
      if (!paymentGroupIds.length) {
        this.$message.info("暂无关联支付订单");
        return;
      }
      this.payOrderDialogVisible = true;
      this.payOrderDialogLoading = true;
      this.payOrderTableData = [];
      payOrderModules
        .fetchPayOrdersByGroupIds({
          paymentGroupIds: JSON.stringify(paymentGroupIds),
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "加载支付订单失败");
            this.payOrderDialogVisible = false;
            return;
          }
          this.payOrderTableData = res.ldata || [];
          if (!this.payOrderTableData.length) {
            this.$message.info("未找到关联支付订单");
          }
        })
        .catch(() => {
          this.$message.error("加载支付订单失败");
          this.payOrderDialogVisible = false;
        })
        .finally(() => {
          this.payOrderDialogLoading = false;
        });
    },
    showOpreateLog(row) {
      this.operateHistory = [];
      this.opreateLogVisible = true;
      requestModules
        .fetchOpreateLogs({
          startTime: 0,
          endTime: new Date().getTime(),
          bussinessId: row.invoiceId,
          beginIndex: 0,
          pageSize: 1000,
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "获取操作记录失败");
            return;
          }
          this.operateHistory = res.ldata || [];
        })
        .catch(err => {
          this.$message.error((err && err.message) || "获取操作记录失败");
        });
    },
    payOrderSpanMethod({ row, column }) {
      if (PAY_ORDER_VIEW_MERGE_PROPS.includes(column.property)) {
        let rowspan = row._paymentRowSpan || 0;
        return { rowspan, colspan: rowspan > 0 ? 1 : 0 };
      }
      return { rowspan: 1, colspan: 1 };
    },
    getPayOrderStatusType(orderStatus) {
      return getPayOrderStatusTagType(orderStatus);
    },
    onVoidInvoice(row) {
      this.$confirm("确认作废该发票？作废后关联支付订单将重新进入未开票。", "提示", { type: "warning" }).then(() => {
        invoiceModules.voidInvoice({ _id: row._id }).then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "作废失败");
            return;
          }
          this.$message.success("作废成功");
          this.getData();
        });
      });
    },
    isInvoiceFileImage(url, fileName) {
      let target = fileName || url || "";
      if (/\.pdf(\?.*)?$/i.test(target)) {
        return false;
      }
      return /\.(jpg|jpeg|png|gif|webp|bmp)(\?.*)?$/i.test(target);
    },
    // 兼容历史单数字段 invoiceFileUrl / invoiceFileName
    resolveInvoiceFileLists(invoice) {
      let urls = Array.isArray(invoice && invoice.invoiceFileUrls) ? invoice.invoiceFileUrls.filter(Boolean) : [];
      let names = Array.isArray(invoice && invoice.invoiceFileNames) ? [...invoice.invoiceFileNames] : [];
      if (!urls.length && invoice && invoice.invoiceFileUrl) {
        urls = [invoice.invoiceFileUrl];
        names = invoice.invoiceFileName ? [invoice.invoiceFileName] : [""];
      }
      while (names.length < urls.length) {
        names.push("");
      }
      return {
        urls,
        names: names.slice(0, urls.length),
      };
    },
    buildInvoiceFileItems(urls, names) {
      let urlList = Array.isArray(urls) ? urls.filter(Boolean) : [];
      let nameList = Array.isArray(names) ? names : [];
      return urlList.map((url, index) => {
        let name = nameList[index] || "";
        return {
          url,
          name,
          isImage: this.isInvoiceFileImage(url, name),
        };
      });
    },
    getInvoiceFileItems(row) {
      if (row && Array.isArray(row._invoiceFileItems)) {
        return row._invoiceFileItems;
      }
      let files = this.resolveInvoiceFileLists(row);
      return this.buildInvoiceFileItems(files.urls, files.names);
    },
    getInvoiceFileDisplayName(row) {
      let items = this.getInvoiceFileItems(row);
      if (!items.length) {
        return "-";
      }
      if (items.length > 1) {
        return `查看发票(${items.length})`;
      }
      if (items[0].name) {
        return items[0].name;
      }
      return items[0].isImage ? "预览图片" : "预览PDF";
    },
    onPreviewInvoiceFile(row) {
      let items = this.getInvoiceFileItems(row);
      if (!items.length) {
        this.$message.info("暂无发票文件");
        return;
      }
      this.invoiceFilePreviewImages = items.filter(item => item.isImage).map(item => item.url);
      let pdfItem = items.find(item => !item.isImage);
      this.invoiceFilePreviewPdfUrl = pdfItem ? pdfItem.url : "";
      this.invoiceFilePreviewVisible = true;
    },
    onUploadInvoiceFile(row) {
      this.invoiceFileUploadRow = row;
      this.invoiceFileUploadItems = this.getInvoiceFileItems(row).map(item => ({ ...item }));
      this.invoiceFileUploadTitle = this.invoiceFileUploadItems.length ? "更新发票" : "上传发票";
      this.invoiceFileUploadVisible = true;
    },
    onInvoiceFileUploadDialogOpened() {
      document.addEventListener("paste", this.handleInvoiceFilePaste);
      this.$nextTick(() => {
        if (this.$refs.invoiceFilePastePanel) {
          this.$refs.invoiceFilePastePanel.focus();
        }
      });
    },
    onInvoiceFileUploadDialogClosed() {
      document.removeEventListener("paste", this.handleInvoiceFilePaste);
      this.invoiceFileUploading = false;
    },
    isInvoiceImageFile(file) {
      if (!file) {
        return false;
      }
      if (file.type && file.type.indexOf("image/") === 0) {
        return true;
      }
      let name = (file.name || "").toLowerCase();
      return /\.(jpg|jpeg|png|gif|webp|bmp)$/.test(name);
    },
    beforeInvoiceFileUpload(file) {
      let isImage = this.isInvoiceImageFile(file);
      let isPdf = (file.type && file.type === "application/pdf") || /\.pdf$/i.test(file.name || "");
      if (!isImage && !isPdf) {
        this.$message.error("仅支持上传图片或 PDF 文件");
        return false;
      }
      let maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$message.error("文件大小不能超过 20MB");
        return false;
      }
      return true;
    },
    handleInvoiceFilePaste(event) {
      if (!this.invoiceFileUploadVisible || this.invoiceFileUploading) {
        return;
      }
      let items = event.clipboardData && event.clipboardData.items;
      if (!items || !items.length) {
        return;
      }
      for (let i = 0; i < items.length; i++) {
        let item = items[i];
        if (item.kind === "file" && item.type.indexOf("image/") === 0) {
          event.preventDefault();
          let file = item.getAsFile();
          if (file) {
            this.uploadInvoiceImageFile(file);
          }
          return;
        }
      }
    },
    uploadInvoiceImageFile(file) {
      if (!this.isInvoiceImageFile(file)) {
        this.$message.error("请粘贴图片文件");
        return;
      }
      let formData = new FormData();
      formData.append("file", file);
      formData.append("bizModule", "invoice");
      this.invoiceFileUploading = true;
      ossModules
        .upload(formData)
        .then(res => {
          if (res && res.objData && res.objData.fileUrl) {
            this.invoiceFileUploadItems.push({
              url: res.objData.fileUrl,
              name: res.objData.originalFileName || file.name || "",
              isImage: true,
            });
            return;
          }
          this.$message.error((res && res.message) || "上传失败");
        })
        .catch(err => {
          this.$message.error((err && err.message) || "上传失败");
        })
        .finally(() => {
          this.invoiceFileUploading = false;
        });
    },
    handleInvoiceFileUploadSuccess(res) {
      if (res && res.objData && res.objData.fileUrl) {
        let url = res.objData.fileUrl;
        let name = res.objData.originalFileName || "";
        this.invoiceFileUploadItems.push({
          url,
          name,
          isImage: this.isInvoiceFileImage(url, name),
        });
        return;
      }
      this.$message.error("上传失败");
    },
    removeInvoiceFileUploadItem(index) {
      this.invoiceFileUploadItems.splice(index, 1);
    },
    saveInvoiceFileUpload() {
      if (!this.invoiceFileUploadRow) {
        return;
      }
      if (!this.invoiceFileUploadItems.length) {
        this.$message.error("请先上传发票文件");
        return;
      }
      let urls = this.invoiceFileUploadItems.map(item => item.url);
      let names = this.invoiceFileUploadItems.map(item => item.name || "");
      let hadFiles = this.getInvoiceFileItems(this.invoiceFileUploadRow).length > 0;
      this.invoiceFileUploadSaving = true;
      invoiceModules
        .updateInvoiceFile({
          _id: this.invoiceFileUploadRow._id,
          invoiceFileUrls: JSON.stringify(urls),
          invoiceFileNames: JSON.stringify(names),
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "保存失败");
            return;
          }
          this.$message.success(hadFiles ? "发票更新成功" : "发票上传成功");
          this.invoiceFileUploadVisible = false;
          this.getData();
        })
        .catch(() => {
          this.$message.error("保存失败");
        })
        .finally(() => {
          this.invoiceFileUploadSaving = false;
        });
    },
    onApproveInvoice(row) {
      if (row.invoiceStatus === INVOICE_STATUS.INVOICE_REVIEW) {
        this.onConfirmInvoice(row);
        return;
      }
      if (row.invoiceStatus === INVOICE_STATUS.PENDING && !this.getInvoiceFileItems(row).length) {
        this.$message.error("请先上传发票文件");
        return;
      }
      let label = this.getInvoicePassButtonLabel(row.invoiceStatus);
      this.$confirm(`确认${label}？`, "提示", { type: "warning" }).then(() => {
        this.updateInvoiceStatusAction(row._id, "pass", `${label}成功`);
      });
    },
    onRejectInvoice(row) {
      const rejectTargetMap = {
        [INVOICE_STATUS.INFO_REVIEW]: "开票信息待确认",
        [INVOICE_STATUS.PENDING]: "待开票信息审核",
        [INVOICE_STATUS.INVOICE_REVIEW]: "待开票",
      };
      this.rejectInvoiceId = row._id;
      this.rejectTargetStatus = rejectTargetMap[row.invoiceStatus] || "待开票";
      this.rejectReason = "";
      this.rejectDialogVisible = true;
    },
    submitRejectInvoice() {
      if (!this.rejectReason || !this.rejectReason.trim()) {
        this.$message.error("请输入打回原因");
        return;
      }
      this.rejectSubmitting = true;
      invoiceModules
        .updateInvoiceStatus({
          _id: this.rejectInvoiceId,
          action: "reject",
          rejectReason: this.rejectReason.trim(),
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "操作失败");
            return;
          }
          this.$message.success("打回成功");
          this.rejectDialogVisible = false;
          this.getData();
        })
        .catch(err => {
          this.$message.error((err && err.message) || "操作失败");
        })
        .finally(() => {
          this.rejectSubmitting = false;
        });
    },
    updateInvoiceStatusAction(invoiceId, action, successText, extraPayload) {
      invoiceModules
        .updateInvoiceStatus({
          _id: invoiceId,
          action,
          ...(extraPayload || {}),
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "操作失败");
            return;
          }
          this.$message.success(successText);
          this.getData();
        })
        .catch(err => {
          this.$message.error((err && err.message) || "操作失败");
        });
    },
    onConfirmInvoice(row) {
      this.confirmInvoiceRow = row;
      this.confirmInvoiceForm = {
        invoiceDate: row.invoiceDate || new Date().format("yyyy-MM-dd"),
      };
      this.confirmInvoiceVisible = true;
    },
    submitConfirmInvoice() {
      if (!this.confirmInvoiceRow) {
        return;
      }
      this.confirmInvoiceSaving = true;
      invoiceModules
        .confirmInvoice({
          _id: this.confirmInvoiceRow._id,
          invoiceDate: this.confirmInvoiceForm.invoiceDate || "",
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "确认开票失败");
            return;
          }
          this.$message.success("确认开票成功");
          this.confirmInvoiceVisible = false;
          this.getData();
        })
        .catch(() => {
          this.$message.error("确认开票失败");
        })
        .finally(() => {
          this.confirmInvoiceSaving = false;
        });
    },
    onDeleteInvoice(row) {
      this.$confirm("确认删除该发票记录？", "提示", { type: "warning" }).then(() => {
        invoiceModules.deleteInvoice({ _id: row._id }).then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "删除失败");
            return;
          }
          this.$message.success("删除成功");
          this.getData();
        });
      });
    },
    getInvoiceSummaries(param) {
      const { columns, data } = param;
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (column.property === "amount") {
          sums[index] = this.formatMoney(data.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0));
          return;
        }
        if (column.property === "taxAmount") {
          sums[index] = this.formatMoney(data.reduce((sum, row) => sum + (parseFloat(row.taxAmount) || 0), 0));
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    getTableSummaries(param) {
      const { columns, data } = param;
      const invoiceFirstRows = (data || []).filter(row => row._invoiceRowSpan > 0);
      const sums = [];
      columns.forEach((column, index) => {
        if (column.type === "selection") {
          sums[index] = "";
          return;
        }
        if (column.label === "序号") {
          sums[index] = "合计";
          return;
        }
        if (column.property === "quantity") {
          sums[index] = (data || []).reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
          return;
        }
        if (["amount", "taxAmount"].includes(column.property)) {
          sums[index] = this.formatMoney(
            (data || []).reduce((sum, row) => sum + (parseFloat(row[column.property]) || 0), 0)
          );
          return;
        }
        if (["totalAmount", "totalTaxAmount", "totalWithTax"].includes(column.property)) {
          sums[index] = this.formatMoney(
            invoiceFirstRows.reduce((sum, row) => sum + (parseFloat(row[column.property]) || 0), 0)
          );
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    saveInvoice() {
      if (this.invoiceEditSupplierInvoiceInfoList.length && !this.invoiceForm.invoiceInfoId) {
        this.$message.warning("请选择开票信息");
        return;
      }
      let selectedInfo = this.selectedInvoiceEditInfo;
      this.invoiceSaving = true;
      invoiceModules
        .addOrUpdateInvoice({
          _id: this.invoiceForm._id,
          invoiceDate: this.invoiceForm.invoiceDate || "",
          remark: this.invoiceForm.remark || "",
          invoiceInfoId: this.invoiceForm.invoiceInfoId || "",
          supplierInvoiceInfo: selectedInfo
            ? JSON.stringify({
                invoiceInfoId: selectedInfo.invoiceInfoId || this.invoiceForm.invoiceInfoId || "",
                invoiceCompanyName: selectedInfo.invoiceCompanyName || "",
                invoiceTaxNo: selectedInfo.invoiceTaxNo || "",
                invoiceCompanyAddress: selectedInfo.invoiceCompanyAddress || "",
                invoicePhone: selectedInfo.invoicePhone || "",
                invoiceBankName: selectedInfo.invoiceBankName || "",
                invoiceBankAccount: selectedInfo.invoiceBankAccount || "",
                invoiceEmail: selectedInfo.invoiceEmail || "",
              })
            : "",
          paymentGroupIds: JSON.stringify(this.invoiceForm.paymentGroupIds || []),
          lines: JSON.stringify(this.invoiceForm.lines || []),
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "保存失败");
            return;
          }
          this.$message.success("保存成功");
          this.invoiceDialogVisible = false;
          this.getData();
        })
        .catch(() => {
          this.$message.error("保存失败");
        })
        .finally(() => {
          this.invoiceSaving = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/styles/purchaseMgmtPage.scss";

#InvoiceMgmt {
  .page-toolbar {
    flex-shrink: 0;
    margin-bottom: 12px;
  }

  .table-container {
    overflow: hidden;
  }

  .mgmt-table {
    .el-table__footer-wrapper td,
    .el-table__fixed-footer-wrapper td {
      background: #fafafa !important;
      font-weight: 600;
      color: #303133;
      font-size: 12px;
    }

    /* 固定列合计与主表合计行对齐，避免「合计」格上下偏移 */
    .el-table__fixed,
    .el-table__fixed-right {
      height: 100% !important;
      bottom: 0 !important;
    }

    .el-table__fixed-footer-wrapper {
      position: absolute;
      left: 0;
      bottom: 0;
      z-index: 4;
    }
  }

  .action-cell {
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  .filter-date {
    width: 240px;
  }

  .invoice-file-link {
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .invoice-file-thumbs {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
  }

  .invoice-file-thumb {
    width: 36px;
    height: 36px;
    border-radius: 4px;
    border: 1px solid #ebeef5;
    overflow: hidden;
  }

  .invoice-file-more {
    font-size: 12px;
    color: #909399;
  }

  .el-table .text-col-20 .cell {
    max-width: 280px;
  }
}

.invoice-action-dropdown {
  .el-dropdown-item.success {
    color: #67c23a;
  }

  .el-dropdown-item.warning {
    color: #e6a23c;
  }

  .el-dropdown-item.danger {
    color: #f56c6c;
  }
}

.pay-order-invoice-dialog {
  .invoice-preview-config {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
    padding: 14px 16px;
    background: #fafafa;
    border: 1px solid #ebeef5;
    border-radius: 8px;
  }

  .invoice-preview-config-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .invoice-preview-config-label {
    width: 88px;
    flex-shrink: 0;
    color: #606266;
    font-size: 13px;
    font-weight: 600;
    text-align: right;
  }

  .invoice-preview-config-value {
    color: #303133;
    font-size: 13px;
  }

  .invoice-preview-config-select {
    width: 420px;
  }

  .invoice-preview-config-tip {
    padding: 10px 12px;
    color: #e6a23c;
    font-size: 12px;
    background: #fdf6ec;
    border: 1px solid #faecd8;
    border-radius: 6px;
  }

  .invoice-preview-info-descriptions {
    width: 100%;
    background: #fff;
  }

  .invoice-preview-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .contract-preview-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: #f5f7fa;
    border: 1px solid #ebeef5;
    border-radius: 8px;
  }

  .contract-preview-meta-label {
    color: #606266;
    font-size: 13px;
    font-weight: 600;
  }

  .contract-preview-meta-value {
    color: #303133;
    font-size: 14px;
    font-weight: 600;
  }

  .invoice-preview-table {
    background: #fff;
  }

  .invoice-preview-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    background: linear-gradient(135deg, #fafafa 0%, #f5f7fa 100%);
    border: 1px solid #ebeef5;
    border-radius: 8px;
  }

  .invoice-preview-footer-label {
    color: #303133;
    font-size: 14px;
    font-weight: 600;
  }

  .invoice-preview-footer-value {
    color: #409eff;
    font-size: 20px;
    font-weight: 700;
  }

  .invoice-edit-extra {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 16px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 8px;
  }

  .invoice-edit-extra-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 240px;

    &.wide {
      flex: 1;
      min-width: 320px;
    }
  }

  .invoice-edit-extra-label {
    flex-shrink: 0;
    width: 68px;
    color: #606266;
    font-size: 13px;
    text-align: right;
  }
}

.invoice-pay-order-dialog {
  .el-table .merge-col {
    vertical-align: middle;
  }

  .positive {
    color: #67c23a;
  }

  .negative {
    color: #f56c6c;
  }

  .zero {
    color: #909399;
  }
}

.invoice-file-upload-dialog {
  .invoice-file-upload-panel {
    outline: none;
  }

  .invoice-file-upload-panel:focus {
    outline: none;
  }

  .invoice-file-upload-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    min-height: 120px;
  }

  .invoice-file-upload-item {
    position: relative;
    width: 120px;
    height: 120px;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #ebeef5;
  }

  .invoice-file-upload-image {
    width: 120px;
    height: 120px;
  }

  .invoice-file-upload-pdf {
    width: 120px;
    height: 120px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px;
    box-sizing: border-box;
    background: #fafafa;
    color: #606266;
    font-size: 12px;
    text-align: center;
    word-break: break-all;

    i {
      font-size: 28px;
      color: #909399;
    }
  }

  .invoice-file-upload-remove {
    position: absolute;
    top: 4px;
    right: 4px;
    font-size: 18px;
    color: #f56c6c;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 50%;
  }

  .invoice-file-upload-add .el-upload {
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
  }

  .invoice-file-upload-add .el-upload:hover {
    border-color: #409eff;
  }

  .invoice-file-upload-trigger {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 120px;
    color: #909399;
    font-size: 12px;
    gap: 8px;

    i {
      font-size: 28px;
      color: #8c939d;
    }
  }

  .invoice-file-upload-tip {
    margin-top: 12px;
    font-size: 12px;
    color: #909399;
    text-align: center;
    line-height: 1.6;
  }
}

.invoice-file-preview-dialog {
  .invoice-file-preview-content {
    min-height: 480px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f7fa;
    border-radius: 8px;
    overflow: auto;
  }

  .invoice-file-preview-body {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .invoice-file-preview-images {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px;
    box-sizing: border-box;
  }

  .invoice-file-preview-full {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
  }

  .invoice-file-preview-frame {
    width: 100%;
    height: 70vh;
    border: none;
    background: #fff;
  }

  .invoice-file-preview-empty {
    color: #909399;
  }
}

.invoice-supplier-info-dialog {
  .invoice-info-header {
    margin-bottom: 12px;
    font-size: 15px;
    font-weight: 600;
    color: #303133;
  }

  .invoice-info-descriptions {
    width: 100%;
  }

  .invoice-view-empty {
    padding: 24px 0;
    text-align: center;
    color: #909399;
    font-size: 13px;
  }
}

.company-invoice-info-dialog {
  .company-invoice-tabs {
    .el-tabs__header {
      margin-bottom: 12px;
    }

    .el-tabs__item {
      font-size: 13px;
    }
  }

  .invoice-info-descriptions {
    width: 100%;
  }
}

.pay-order-reject-dialog {
  .reject-dialog-tip {
    margin-bottom: 12px;
    color: #606266;
    font-size: 13px;
  }
}

.remark-cell {
  line-height: 1.5;
  word-break: break-all;

  .reject-reason-text {
    margin-top: 4px;
    color: #e6a23c;
    font-size: 12px;
  }
}
</style>
